import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useNotificationContext } from '../contexts/NotificationContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const Grades = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, gradeId: null });
  const [formData, setFormData] = useState({
    name: '',
    abbreviation: ''
  });

  useEffect(() => {
    fetchGrades();
  }, []);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'connection') {
      console.log('Connection message:', data.message);
    } else if (data.type === 'grade_updated') {
      console.log('🔄 Grade update reçu:', data.data?.action);
      fetchGrades();
    }
  }, []);

  // Use WebSocket context
  const { isConnected } = useWebSocket('grades', handleWebSocketMessage);

  const fetchGrades = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/grades?limit=1000&_t=${Date.now()}`);
      setGrades(Array.isArray(response.data) ? response.data : response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des grades:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      if (editingGrade) {
        await axios.put(`${API_URL}/api/grades/${editingGrade._id}`, formData, config);
        showSuccess('Grade modifié avec succès');
      } else {
        await axios.post(`${API_URL}/api/grades`, formData, config);
        showSuccess('Grade ajouté avec succès');
      }
      closeModal();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showError('Erreur lors de la sauvegarde du grade');
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      await axios.delete(`${API_URL}/api/grades/${id}`, config);
      showSuccess('Grade supprimé avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showError('Erreur lors de la suppression du grade');
    }
  };

  const confirmDelete = (gradeId) => {
    setDeleteConfirm({ show: true, gradeId });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.gradeId) {
      await handleDelete(deleteConfirm.gradeId);
    }
    setDeleteConfirm({ show: false, gradeId: null });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, gradeId: null });
  };

  const openModal = (grade = null) => {
    setEditingGrade(grade);
    setFormData(grade ? {
      name: grade.name,
      abbreviation: grade.abbreviation
    } : {
      name: '',
      abbreviation: ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGrade(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <h1 className="text-xl font-semibold text-gray-900">Grades</h1>
            <span className={`px-2 py-1 text-xs rounded-full ${
              isConnected 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {isConnected ? '🟢 Live' : '🔴 Manual'}
            </span>
          </div>
          <button 
            onClick={() => openModal()} 
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
          >
            + Ajouter
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {grades.map(grade => (
            <div key={grade._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🏅</span>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{grade.name}</h3>
                    <p className="text-sm text-gray-600">
                      Abréviation: <span className="font-medium">{grade.abbreviation}</span>
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button 
                    onClick={() => openModal(grade)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  >
                    ✏️
                  </button>
                  <button 
                    onClick={() => confirmDelete(grade._id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {grades.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🏅</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun grade</h3>
            <p className="text-gray-600">Cliquez sur "Ajouter" pour commencer.</p>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">{editingGrade ? 'Modifier Grade' : 'Ajouter Grade'}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du grade</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ex: Professeur, Maître de conférences..."
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Abréviation</label>
                  <input
                    type="text"
                    name="abbreviation"
                    value={formData.abbreviation}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ex: Prof, MCF..."
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                  >
                    {editingGrade ? 'Modifier' : 'Ajouter'}
                  </button>
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-red-600">🗑️ Confirmer la suppression</h3>
                <button onClick={cancelDelete} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 mb-4">Êtes-vous sûr de vouloir supprimer ce grade ? Cette action est irréversible.</p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Grades;